import { useState } from 'react'
import { Flag } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { Button } from '@/shared/ui/button'
import { SegmentedControl } from '@/shared/ui/segmented'
import { useAuthStore } from '@/core/auth/auth-store'
import { useMyProfile } from '@/features/perfil/use-my-profile'
import { showToast } from '@/shared/ui/toast'
import { fileAbuseReport } from '@/features/admin/api/abuse-reports-repository'
import { ABUSE_DETAIL_MAX, type AbuseReportType } from '@/core/models/abuse-report'
import { logger } from '@upadelmatch/core'

/**
 * Reporting a player — the half of moderation that did not exist.
 *
 * The /admin queue was built and shipped against a mock store, so there was an
 * operator surface for reports nobody could file. This is the source.
 *
 * WHAT IT PROMISES, AND WHAT IT DOES NOT. It says the report was sent, and
 * nothing more. A player cannot read the queue (rules give `get`/`list` to
 * operators alone, because reports name people and quote accusations), so
 * promising a status they will never see would be a lie the app tells once and
 * then cannot keep. "We'll look at it" is the honest ceiling.
 *
 * TWO TYPES because they resolve differently. A disputed absence has a number
 * behind it — upholding it gives a no-show back — while conduct has no counter
 * to correct. Collapsing them into one free-text box would push that judgement
 * onto the operator every single time.
 */
export function ReportPlayerSheet({
  open,
  onClose,
  reportedUid,
  reportedName,
  matchId,
}: {
  open: boolean
  onClose: () => void
  reportedUid: string | null
  reportedName: string | null
  matchId?: string | null
}) {
  const me = useAuthStore((s) => s.user?.uid ?? null)
  const myName = useMyProfile().data?.profile.name ?? null
  const [type, setType] = useState<AbuseReportType>('behaviour')
  const [detail, setDetail] = useState('')
  const [sending, setSending] = useState(false)

  const canSend = Boolean(me && reportedUid && detail.trim().length >= 10 && !sending)

  async function send() {
    if (!me || !reportedUid) return
    setSending(true)
    try {
      await fileAbuseReport({
        reporterUid: me,
        reporterName: myName,
        reportedUid,
        reportedName,
        type,
        matchId: matchId ?? null,
        detail,
      })
      // Deliberately does not say "resolved" or give a reference number — see
      // the note above. It confirms receipt, which is all we can honour.
      showToast('Denuncia enviada. La revisaremos.')
      setDetail('')
      onClose()
    } catch (err) {
      logger.w('[abuse] file failed', err)
      showToast('No se pudo enviar. Inténtalo de nuevo.')
    } finally {
      setSending(false)
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title={`Denunciar a ${reportedName ?? 'jugador'}`}>
      <div className="flex flex-col gap-4 pb-2">
        <SegmentedControl<AbuseReportType>
          value={type}
          onChange={setType}
          options={[
            { value: 'behaviour', label: 'Comportamiento' },
            { value: 'no_show_disputed', label: 'Ausencia' },
          ]}
        />

        <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
          {type === 'no_show_disputed'
            ? 'Si te marcaron como ausente y sí estuviste, cuéntanoslo: si tienes razón, te devolvemos la ausencia.'
            : 'Cuéntanos qué pasó. Lo revisa una persona.'}
        </p>

        <div className="flex flex-col gap-1">
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value.slice(0, ABUSE_DETAIL_MAX))}
            rows={4}
            placeholder="Qué pasó, cuándo y en qué partido."
            className="w-full rounded-[var(--radius)] border border-[var(--line)] bg-[var(--panel-2)] p-3 text-[length:var(--fs-base)]"
          />
          {/* The counter appears near the limit rather than always: a permanent
              counter reads as a demand for length on a form nobody wants to be
              filling in. */}
          {detail.length > ABUSE_DETAIL_MAX - 100 && (
            <span className="self-end text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
              {detail.length}/{ABUSE_DETAIL_MAX}
            </span>
          )}
        </div>

        <Button onClick={() => void send()} disabled={!canSend}>
          <Flag size={17} /> {sending ? 'Enviando…' : 'Enviar denuncia'}
        </Button>
        {/* Says why the button is dead instead of leaving a disabled control
            with no explanation — the "visible control that does nothing" defect
            the design rubric fails a screen for. */}
        {detail.trim().length < 10 && (
          <p className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)] text-center">
            Escribe al menos una frase para que podamos revisarlo.
          </p>
        )}
      </div>
    </BottomSheet>
  )
}
